import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BalanceIcon from "@mui/icons-material/Balance";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import PreviewIcon from "@mui/icons-material/Preview";
import ScheduleIcon from "@mui/icons-material/Schedule";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
const DECIMAL_PATTERN = /^\d*(\.\d{0,2})?$/;

const money = (value) => {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
};

const signedMoney = (value) => {
  const n = Number(value || 0);
  if (Number.isNaN(n) || n === 0) return "$0.00";
  const sign = n > 0 ? "+" : "-";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const PAYMENT_FIELDS = [
  {
    key: "creditCards",
    label: "Credit Cards",
    hint: "Total charged on credit/debit cards today",
    icon: CreditCardIcon,
    color: "#1565c0",
    bg: "#e3f2fd",
  },
  {
    key: "checks",
    label: "Checks Received",
    hint: "Total received in checks today",
    icon: ReceiptLongIcon,
    color: "#e65100",
    bg: "#fff3e0",
  },
  {
    key: "giftCards",
    label: "Gift Cards",
    hint: "Total received from gift cards today",
    icon: CardGiftcardIcon,
    color: "#7b1fa2",
    bg: "#f3e5f5",
  },
];

const diffColor = (diff) => {
  if (diff > 0) return "#2e7d32";
  if (diff < 0) return "#c62828";
  return "#616161";
};

const statusConfig = (status) => {
  switch (status) {
    case "Overage":
      return {
        icon: <TrendingUpIcon />,
        color: "#e65100",
        bg: "#fff3e0",
        border: "#ffcc80",
      };
    case "Shortage":
      return {
        icon: <TrendingDownIcon />,
        color: "#c62828",
        bg: "#ffebee",
        border: "#ef9a9a",
      };
    default:
      return {
        icon: <CheckCircleOutlineIcon />,
        color: "#2e7d32",
        bg: "#e8f5e9",
        border: "#a5d6a7",
      };
  }
};

const NIGHT_SKY = `
  radial-gradient(1px 1px at 12% 18%, rgba(255,255,255,0.7) 0%, transparent 100%),
  radial-gradient(1px 1px at 28% 62%, rgba(255,255,255,0.5) 0%, transparent 100%),
  radial-gradient(1.5px 1.5px at 45% 8%, rgba(255,255,255,0.8) 0%, transparent 100%),
  radial-gradient(1px 1px at 62% 38%, rgba(255,255,255,0.6) 0%, transparent 100%),
  radial-gradient(1px 1px at 78% 72%, rgba(255,255,255,0.5) 0%, transparent 100%),
  radial-gradient(1.5px 1.5px at 88% 22%, rgba(255,255,255,0.7) 0%, transparent 100%),
  radial-gradient(1px 1px at 35% 85%, rgba(255,255,255,0.4) 0%, transparent 100%),
  radial-gradient(1px 1px at 55% 52%, rgba(255,255,255,0.5) 0%, transparent 100%),
  radial-gradient(1px 1px at 95% 55%, rgba(255,255,255,0.45) 0%, transparent 100%),
  radial-gradient(1px 1px at 8% 78%, rgba(255,255,255,0.4) 0%, transparent 100%),
  linear-gradient(175deg, #0a0e27 0%, #141852 30%, #1b2460 50%, #1a1a4e 70%, #0d0f2b 100%)
`;

export default function RegisterCloseoutDialog({
  open,
  onClose,
  onConfirm,
  openedAt,
  closingAt,
  previousCashOnHand = 200,
  accountedCashSales = 0,
  totalCashOuts = 0,
  totalPayIns = 0,
  expectedTotals,
  loading = false,
  modalZIndex,
  locationName,
}) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    cashInDrawer: "",
    creditCards: "",
    checks: "",
    giftCards: "",
  });
  const [errors, setErrors] = useState({});
  const isDark = useTheme().palette.mode === "dark";
  const panelBg = isDark ? "#141416" : "#fafafa";
  const line = isDark ? "rgba(255,255,255,0.12)" : "#e0e0e0";
  const ink = isDark ? "#f2f2f2" : "#37474f";

  const expectedCashInDrawer = +(
    previousCashOnHand +
    accountedCashSales -
    totalCashOuts +
    totalPayIns
  ).toFixed(2);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors({});
    setFormData({
      cashInDrawer: String(expectedCashInDrawer),
      creditCards: String(expectedTotals?.creditCards ?? ""),
      checks: String(expectedTotals?.checks ?? ""),
      giftCards: String(expectedTotals?.giftCards ?? ""),
    });
  }, [open, expectedTotals, expectedCashInDrawer]);

  const normalized = useMemo(
    () => ({
      cashInDrawer: Number(formData.cashInDrawer || 0),
      creditCards: Number(formData.creditCards || 0),
      checks: Number(formData.checks || 0),
      giftCards: Number(formData.giftCards || 0),
    }),
    [formData]
  );

  const cashDeposited = useMemo(() => {
    return +(
      previousCashOnHand +
      accountedCashSales -
      totalCashOuts +
      totalPayIns -
      normalized.cashInDrawer
    ).toFixed(2);
  }, [
    previousCashOnHand,
    accountedCashSales,
    totalCashOuts,
    totalPayIns,
    normalized.cashInDrawer,
  ]);

  const allFieldKeys = ["cashInDrawer", "creditCards", "checks", "giftCards"];

  const totals = useMemo(() => {
    const expectedTotal =
      expectedCashInDrawer +
      Number(expectedTotals?.creditCards || 0) +
      Number(expectedTotals?.checks || 0) +
      Number(expectedTotals?.giftCards || 0);

    const reportedTotal =
      normalized.cashInDrawer +
      normalized.creditCards +
      normalized.checks +
      normalized.giftCards;

    const difference = +(reportedTotal - expectedTotal).toFixed(2);
    const status =
      difference === 0 ? "Matching" : difference > 0 ? "Overage" : "Shortage";

    return { expectedTotal, reportedTotal, difference, status };
  }, [expectedTotals, expectedCashInDrawer, normalized]);

  const perFieldDiff = useMemo(() => {
    const expected = {
      cashInDrawer: expectedCashInDrawer,
      creditCards: Number(expectedTotals?.creditCards || 0),
      checks: Number(expectedTotals?.checks || 0),
      giftCards: Number(expectedTotals?.giftCards || 0),
    };
    const result = {};
    allFieldKeys.forEach((key) => {
      result[key] = +((normalized[key] || 0) - (expected[key] || 0)).toFixed(2);
    });
    return result;
  }, [normalized, expectedTotals, expectedCashInDrawer]);

  const validate = () => {
    const nextErrors = {};
    allFieldKeys.forEach((key) => {
      const value = formData[key];
      if (value === "") {
        nextErrors[key] = "Required";
        return;
      }
      if (!DECIMAL_PATTERN.test(value)) {
        nextErrors[key] = "Enter a valid number (up to 2 decimals)";
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (key, raw) => {
    if (raw === "" || DECIMAL_PATTERN.test(raw)) {
      setFormData((prev) => ({ ...prev, [key]: raw }));
      if (errors[key]) {
        setErrors((prev) => ({ ...prev, [key]: undefined }));
      }
    }
  };

  const handleReview = () => {
    if (loading) return;
    if (!validate()) return;
    setStep(1);
  };

  const handleConfirm = () => {
    if (loading) return;
    onConfirm({
      openedAt,
      closingAt,
      cashInDrawer: normalized.cashInDrawer,
      cashDeposited,
      creditCards: normalized.creditCards,
      checks: normalized.checks,
      giftCards: normalized.giftCards,
      previousCashOnHand,
      accountedCashSales,
      expectedTotals,
      totals,
    });
  };

  const scBase = statusConfig(totals.status);
  const sc = isDark
    ? { ...scBase, bg: `${scBase.color}22`, border: `${scBase.color}55` }
    : scBase;

  const renderDiffBadge = (key) => {
    const diff = perFieldDiff[key];
    const dc = diffColor(diff);
    const val = formData[key];
    if (diff !== 0)
      return (
        <span style={{ color: dc, fontWeight: 600 }}>{signedMoney(diff)}</span>
      );
    if (val !== "")
      return <span style={{ color: "#2e7d32", fontWeight: 600 }}>Matches</span>;
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={modalZIndex != null ? { zIndex: modalZIndex } : undefined}
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <Box
        sx={{
          background: NIGHT_SKY,
          color: "#fff",
          px: 3,
          py: 2.5,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -30,
            right: -10,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,200,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NightsStayIcon sx={{ fontSize: 30, color: "#ffd54f" }} />
          <div>
            <Typography
              component="div"
              sx={{ fontWeight: 700, fontSize: "1.15rem", lineHeight: 1.3 }}
            >
              {step === 0
                ? locationName
                  ? `Register Closing · ${locationName}`
                  : "Register Closing / End of Day"
                : locationName
                  ? `Review Closeout · ${locationName}`
                  : "Review Register Closeout"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.75,
                bgcolor: "rgba(255,255,255,0.08)",
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <ScheduleIcon sx={{ fontSize: 15, color: "#a5d6a7" }} />
              <Typography
                component="span"
                sx={{ fontSize: "0.78rem", color: "#a5d6a7", fontWeight: 600 }}
              >
                {openedAt}
              </Typography>
              <ArrowRightAltIcon
                sx={{ fontSize: 18, color: "rgba(255,255,255,0.4)", mx: 0.25 }}
              />
              <NightsStayIcon sx={{ fontSize: 14, color: "#ef9a9a" }} />
              <Typography
                component="span"
                sx={{ fontSize: "0.78rem", color: "#ef9a9a", fontWeight: 600 }}
              >
                {closingAt}
              </Typography>
            </Box>
          </div>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}
        {step === 0 ? (
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Enter closeout totals for the register period from{" "}
              <strong>{openedAt}</strong> to <strong>{closingAt}</strong>. Each
              field defaults to the system-accounted amount.
            </Typography>

            {/* ── Cash section ── */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: line,
                borderRadius: 2,
                bgcolor: panelBg,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: isDark ? "#2a2a2e" : "#37474f",
                    color: "#fff",
                  }}
                >
                  <AttachMoneyIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="body2" fontWeight={700} sx={{ color: ink }}>
                  Cash
                </Typography>
              </Box>

              {/* Info row: yesterday + today's sales */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 100,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: line,
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Starting Float
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {money(previousCashOnHand)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 100,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: line,
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Cash Sales
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {money(accountedCashSales)}
                  </Typography>
                </Box>
                {totalCashOuts > 0 && (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 100,
                      bgcolor: "background.paper",
                      border: "1px solid #ffcc80",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Payout
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="#e65100">
                      −{money(totalCashOuts)}
                    </Typography>
                  </Box>
                )}
                {totalPayIns > 0 && (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 100,
                      bgcolor: "background.paper",
                      border: "1px solid #a5d6a7",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Pay in
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="#2e7d32">
                      +{money(totalPayIns)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 100,
                    bgcolor: "background.paper",
                    border: "1px dashed",
                    borderColor: isDark ? "rgba(255,255,255,0.28)" : "#bdbdbd",
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Expected
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: ink }}>
                    {money(expectedCashInDrawer)}
                  </Typography>
                </Box>
              </Box>

              {/* Input */}
              <TextField
                label="Cash in Drawer (counted)"
                value={formData.cashInDrawer}
                onChange={(e) => handleChange("cashInDrawer", e.target.value)}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ inputMode: "decimal" }}
                placeholder="0.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    "&.Mui-focused fieldset": { borderColor: "#37474f" },
                  },
                }}
              />
            </Box>

            {/* ── Other payment fields ── */}
            {PAYMENT_FIELDS.map((field) => {
              const Icon = field.icon;
              return (
                <Box key={field.key}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: isDark ? `${field.color}33` : field.bg,
                        color: field.color,
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: field.color }}
                    >
                      {field.label}
                    </Typography>
                  </Box>
                  <TextField
                    value={formData[field.key]}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    error={Boolean(errors[field.key])}
                    helperText={
                      errors[field.key] || (
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>
                            Accounted: {money(expectedTotals?.[field.key])}
                          </span>
                          {renderDiffBadge(field.key)}
                        </Box>
                      )
                    }
                    fullWidth
                    size="small"
                    disabled={loading}
                    inputProps={{ inputMode: "decimal" }}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: field.color,
                        },
                      },
                    }}
                  />
                </Box>
              );
            })}

            {/* Balance status */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                bgcolor: sc.bg,
                border: `1px solid ${sc.border}`,
              }}
            >
              <Avatar sx={{ bgcolor: sc.color, width: 36, height: 36 }}>
                {sc.icon}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={700} color={sc.color}>
                  {totals.status}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Reported {money(totals.reportedTotal)} vs Accounted{" "}
                  {money(totals.expectedTotal)}
                </Typography>
              </Box>
              <Chip
                label={signedMoney(totals.difference)}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: sc.color,
                  color: "#fff",
                  fontSize: "0.8rem",
                }}
              />
            </Box>
          </Stack>
        ) : (
          /* ────────── REVIEW STEP ────────── */
          <Stack spacing={0}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Closing register for <strong>{openedAt}</strong> to{" "}
              <strong>{closingAt}</strong>. Confirming will close out all
              financials and appointments for this period.
            </Typography>

            {/* Cash in Drawer row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 1.5,
                px: 1,
                borderBottom: "1px solid",
                borderColor: isDark ? "rgba(255,255,255,0.08)" : "#f0f0f0",
                "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#fafafa" },
                borderRadius: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? "#2a2a2e" : "#eceff1",
                  color: ink,
                  mr: 1.5,
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  Cash in Drawer
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Expected: {money(expectedCashInDrawer)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" fontWeight={700}>
                  {money(normalized.cashInDrawer)}
                </Typography>
                {perFieldDiff.cashInDrawer !== 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: diffColor(perFieldDiff.cashInDrawer),
                      fontWeight: 600,
                    }}
                  >
                    {signedMoney(perFieldDiff.cashInDrawer)}
                  </Typography>
                )}
                {perFieldDiff.cashInDrawer === 0 &&
                  formData.cashInDrawer !== "" && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#43a047", fontWeight: 600 }}
                    >
                      Matches
                    </Typography>
                  )}
              </Box>
            </Box>

            {/* Other payment rows */}
            {PAYMENT_FIELDS.map((field, idx) => {
              const Icon = field.icon;
              const diff = perFieldDiff[field.key];
              const dc = diffColor(diff);
              return (
                <Box
                  key={field.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 1.5,
                    px: 1,
                    borderBottom:
                      idx < PAYMENT_FIELDS.length - 1
                        ? "1px solid"
                        : "none",
                    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#f0f0f0",
                    "&:hover": {
                      bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#fafafa",
                    },
                    borderRadius: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: isDark ? `${field.color}33` : field.bg,
                      color: field.color,
                      mr: 1.5,
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {field.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Accounted: {money(expectedTotals?.[field.key])}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="body2" fontWeight={700}>
                      {money(normalized[field.key])}
                    </Typography>
                    {diff !== 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ color: dc, fontWeight: 600 }}
                      >
                        {signedMoney(diff)}
                      </Typography>
                    ) : (
                      formData[field.key] !== "" && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#2e7d32", fontWeight: 600 }}
                        >
                          Matches
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              );
            })}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ bgcolor: panelBg, borderRadius: 2, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BalanceIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    Accounted Total
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {money(totals.expectedTotal)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PointOfSaleIcon
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Reported Total
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {money(totals.reportedTotal)}
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar sx={{ bgcolor: sc.color, width: 28, height: 28 }}>
                    {React.cloneElement(sc.icon, { sx: { fontSize: 16 } })}
                  </Avatar>
                  <Typography variant="body2" fontWeight={700}>
                    Balance
                  </Typography>
                </Box>
                <Chip
                  label={`${totals.status}  ${signedMoney(totals.difference)}`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: sc.bg,
                    color: sc.color,
                    border: `1px solid ${sc.border}`,
                    fontSize: "0.8rem",
                  }}
                />
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: line,
          bgcolor: panelBg,
        }}
      >
        {step === 1 ? (
          <>
            <Button
              onClick={() => setStep(0)}
              startIcon={<ArrowBackIcon />}
              sx={{ mr: "auto" }}
              disabled={loading}
            >
              Back
            </Button>
            <Button onClick={onClose} color="inherit" disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirm}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <LockIcon />
                )
              }
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {loading ? "Closing…" : locationName ? `Confirm & Close ${locationName}` : "Confirm & Close Register"}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ mr: "auto" }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleReview}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <PreviewIcon />
                )
              }
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {loading ? "Loading…" : "Review Totals"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
